// src/components/DownloadJourneyButton.js
import React, { useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { marked } from "marked";
import domtoimage from "dom-to-image";

// ---------- Helpers ----------

// Escape plain text for safe HTML
function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Convert plain text with newlines to <p>…<br/>…</p>
function textToParagraphHtml(text) {
  if (!text) return "<p>(none)</p>";
  const safe = escapeHtml(text);
  const withBreaks = safe.replace(/\r?\n/g, "<br />");
  return `<p>${withBreaks}</p>`;
}

// Render an array of plain strings as <ul><li>…</li></ul>
function arrayToListHtml(arr) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return "<p>(none)</p>";
  }
  const items = arr
    .map((item) => `<li>${escapeHtml(String(item))}</li>`)
    .join("");
  return `<ul>${items}</ul>`;
}

// ---------- Code & catalog helpers ----------

// Normalise an MLU code like "edu 101", "EDU-101", "EDU101" → "EDU-101"
function normaliseCode(rawCode) {
  const s = String(rawCode || "").trim();
  if (!s) return "";
  const match = s.match(/^(EDU|EID|GOV|TEC)[\s-]?(\d{3})/i);
  if (match) {
    return `${match[1].toUpperCase()}-${match[2]}`;
  }
  return s.toUpperCase();
}

// Build a lookup: { "EDU-101": { code, title, learningOutcome } }
function buildCatalogLookup(catalogByFramework) {
  const map = {};
  if (!catalogByFramework) return map;

  const frameworks = ["education", "ethics", "governance", "technical"];

  frameworks.forEach((fw) => {
    const arr = catalogByFramework[fw];
    if (!arr || !Array.isArray(arr)) return;

    arr.forEach((item) => {
      const rawCode =
        item.code || item.id || item.mluCode || item.shortCode || "";
      const normCode = normaliseCode(rawCode);
      if (!normCode) return;

      const title =
        item.title || item.name || item.mluName || item.label || "";
      const lo =
        item.learningOutcome ||
        item.learning_outcome ||
        item.learning_outcomes ||
        item.outcome ||
        item.lo ||
        "";

      map[normCode] = {
        code: normCode,
        title,
        learningOutcome: lo,
      };
    });
  });

  return map;
}

// Build a lookup from Step 5 suggestion details: { "EDU-103": { code, title, reason } }
function buildSuggestionLookup(suggestionDetails) {
  const map = {};
  if (!suggestionDetails || !Array.isArray(suggestionDetails)) return map;

  suggestionDetails.forEach((item) => {
    const rawCode =
      item.code || item.id || item.mluCode || item.shortCode || item.key || "";
    const normCode = normaliseCode(rawCode);
    if (!normCode) return;

    const title =
      item.title || item.name || item.mluName || item.label || "";
    const reason =
      item.reason ||
      item.rationale ||
      item.explanation ||
      item.justification ||
      item.why ||
      item.comment ||
      "";

    map[normCode] = {
      code: normCode,
      title,
      reason,
    };
  });

  return map;
}

// Build AI baseline per framework from suggestionDetails (flattened shortlistPreview)
function buildAiBaselineByFramework(suggestionDetails) {
  const baseline = {
    education: new Set(),
    ethics: new Set(),
    governance: new Set(),
    technical: new Set(),
  };

  if (!suggestionDetails || !Array.isArray(suggestionDetails)) {
    return baseline;
  }

  suggestionDetails.forEach((item) => {
    const rawCode =
      item.code || item.id || item.mluCode || item.shortCode || item.key || "";
    const code = normaliseCode(rawCode);
    if (!code) return;

    if (code.startsWith("EDU-")) {
      baseline.education.add(code);
    } else if (code.startsWith("EID-")) {
      baseline.ethics.add(code);
    } else if (code.startsWith("GOV-")) {
      baseline.governance.add(code);
    } else if (code.startsWith("TEC-")) {
      baseline.technical.add(code);
    }
  });

  return baseline;
}

// ---------- Normalisation for MLUs ----------

function normaliseMluItem(raw, catalogLookup, suggestionLookup) {
  if (!raw) {
    return { code: "", title: "", reason: "", selectionType: "" };
  }

  let code = "";
  let title = "";
  let reason = "";
  let selectionType = "";

  if (typeof raw === "object") {
    code =
      raw.code || raw.id || raw.mluCode || raw.shortCode || raw.key || "";
    title =
      raw.title || raw.name || raw.mluName || raw.label || "";
    reason =
      raw.reason ||
      raw.rationale ||
      raw.explanation ||
      raw.justification ||
      raw.why ||
      raw.comment ||
      "";
    selectionType =
      raw.selectionType ||
      raw.origin ||
      raw.source ||
      raw.selection_type ||
      "";
  } else {
    // If we only get a code-like string ("EDU-101"), treat it entirely as code.
    code = String(raw).trim();
  }

  const normCode = normaliseCode(code);

  // Enrich from suggestion details (Step 5)
  const suggestion = suggestionLookup && suggestionLookup[normCode];
  if (suggestion) {
    if (!title) title = suggestion.title || title;
    if (!reason) reason = suggestion.reason || reason;
  }

  // Enrich from catalog (for titles)
  const catalogItem = catalogLookup && catalogLookup[normCode];
  if (catalogItem) {
    title = title || catalogItem.title || "";
  }

  return {
    code: normCode || code,
    title,
    reason,
    selectionType,
  };
}

// ---------- Render helpers for tables ----------

// Step 4 / generic: MLU selection table (code + title)
function renderMluSelectionTable(arr, catalogLookup, suggestionLookup) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return "<p>(none selected)</p>";
  }

  const rows = arr
    .map((raw) => {
      const { code, title } = normaliseMluItem(
        raw,
        catalogLookup,
        suggestionLookup
      );
      const safeCode = escapeHtml(code || "");
      const safeTitle = escapeHtml(
        title || "(no title available in catalog)"
      );
      return `
        <tr>
          <td>${safeCode || "(n/a)"}</td>
          <td>${safeTitle}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th>MLU code</th>
          <th>MLU title</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

// Step 5: suggested MLUs with reasons
function renderSuggestedMluTable(arr, catalogLookup, suggestionLookup) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return "<p>(no suggestions recorded)</p>";
  }

  const rows = arr
    .map((raw) => {
      const { code, title, reason } = normaliseMluItem(
        raw,
        catalogLookup,
        suggestionLookup
      );
      const safeCode = escapeHtml(code || "");
      const safeTitle = escapeHtml(
        title || "(no title available in catalog)"
      );
      const safeReason = textToParagraphHtml(
        reason || "(no explicit reason recorded from the K8 LJD Agent)"
      );
      return `
        <tr>
          <td>${safeCode || "(n/a)"}</td>
          <td>${safeTitle}</td>
          <td>${safeReason}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th>MLU code</th>
          <th>MLU title</th>
          <th>Reason given by the K8 LJD Agent</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

// Step 6: final curated selection with selection type
function renderStep6SelectionTable(
  arr,
  catalogLookup,
  suggestionLookup,
  aiBaselineSet
) {
  if (!arr || !Array.isArray(arr) || arr.length === 0) {
    return "<p>(no MLUs selected in this framework)</p>";
  }

  const aiSet = aiBaselineSet || new Set();

  // Normalise all MLUs first so we have codes and titles
  const items = arr.map((raw) =>
    normaliseMluItem(raw, catalogLookup, suggestionLookup)
  );
  const finalCodes = items.map((item) => item.code).filter(Boolean);

  const finalSet = new Set(finalCodes);
  let intersectionCount = 0;
  finalSet.forEach((code) => {
    if (aiSet.has(code)) intersectionCount++;
  });

  let frameworkSelectionType = "";
  if (finalCodes.length === 0) {
    frameworkSelectionType = "";
  } else if (
    intersectionCount === finalCodes.length &&
    finalCodes.length === aiSet.size &&
    aiSet.size > 0
  ) {
    frameworkSelectionType = "AI";
  } else if (intersectionCount === 0) {
    frameworkSelectionType = "human-only";
  } else {
    frameworkSelectionType = "hybrid";
  }

  const rows = items
    .map((item) => {
      const safeCode = escapeHtml(item.code || "");
      const safeTitle = escapeHtml(
        item.title || "(no title available in catalog)"
      );

      let perMluType = "";
      if (!frameworkSelectionType) {
        perMluType = "(not specified: AI / hybrid / human-only)";
      } else if (frameworkSelectionType === "AI") {
        perMluType = "AI";
      } else if (frameworkSelectionType === "human-only") {
        perMluType = "human-only";
      } else {
        // hybrid framework
        if (aiSet.has(item.code)) {
          perMluType = "hybrid";
        } else {
          perMluType = "human-only";
        }
      }

      const safeType = escapeHtml(
        perMluType || "(not specified: AI / hybrid / human-only)"
      );

      return `
        <tr>
          <td>${safeCode || "(n/a)"}</td>
          <td>${safeTitle}</td>
          <td>${safeType}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th>MLU code</th>
          <th>MLU title</th>
          <th>Selection type (AI / hybrid / human-only)</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

// Catalog section: code + title + learning outcome
function renderCatalogTable(catalogArr) {
  if (!catalogArr || !Array.isArray(catalogArr) || catalogArr.length === 0) {
    return "<p>(catalog entries not available)</p>";
  }

  const rows = catalogArr
    .map((item) => {
      const code = normaliseCode(
        item.code || item.id || item.mluCode || item.shortCode || ""
      );
      const title =
        item.title || item.name || item.mluName || item.label || "";
      const lo =
        item.learningOutcome ||
        item.learning_outcome ||
        item.learning_outcomes ||
        item.outcome ||
        item.lo ||
        "";

      const safeCode = escapeHtml(code);
      const safeTitle = escapeHtml(title);
      const safeLo = textToParagraphHtml(
        lo || "(no learning outcome provided)"
      );

      return `
        <tr>
          <td>${safeCode}</td>
          <td>${safeTitle}</td>
          <td>${safeLo}</td>
        </tr>
      `;
    })
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th>MLU code</th>
          <th>MLU title</th>
          <th>Learning outcome</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
}

// ---------- HTML document builder ----------

function buildJourneyHtmlDoc(finalMd, journeySummary, mapImageData) {
  // Teaching plan (Markdown → HTML)
  const planHtml =
    finalMd && finalMd.trim()
      ? marked.parse(finalMd)
      : "<p>(No teaching plan was generated.)</p>";

  // Unpack structured journey data (defensively)
  const {
    orgGoals,
    learnerProfile,
    futureRoles,
    expectedContribution,
    currentKnowledge,
    suggestedMlus, // per-framework arrays (codes / objects)
    suggestionDetails, // raw shortlistPreview from Step 5 (for reasons + AI baseline)
    customisationNotes,
    step6SelectedMlus, // { education, ethics, governance, technical }
    feedback,
    catalogByFramework,
  } = journeySummary || {};

  // Build lookup maps
  const catalogLookup = buildCatalogLookup(catalogByFramework);
  const suggestionLookup = buildSuggestionLookup(suggestionDetails);
  const aiBaselineByFramework = buildAiBaselineByFramework(suggestionDetails);

  // ---- 2. Session context & logs (Steps 1–6) ----
  let logsHtml = "";

  // Step 1 — Org goals
  logsHtml += `
    <h3>2.1 Step 1 – Organisational Goals</h3>
    ${textToParagraphHtml(
    orgGoals || "(no organisational goals were recorded)"
  )}
  `;

  // Step 2 — Learner profile
  logsHtml += `
    <h3>2.2 Step 2 – Learner Profile</h3>
    ${textToParagraphHtml(
    learnerProfile || "(no learner profile information was recorded)"
  )}
  `;

  // Step 3 — Future roles + expected contribution
  logsHtml += `
    <h3>2.3 Step 3 – Future Roles & Expected Contribution</h3>
    <p><strong>Future roles selected:</strong></p>
    ${arrayToListHtml(futureRoles || [])}
  `;

  if (expectedContribution) {
    logsHtml += `
      <p><strong>Expected contribution of the learner in their organisation:</strong></p>
      ${textToParagraphHtml(expectedContribution)}
    `;
  }

  // Step 4 — Current knowledge & selected MLUs (with full names)
  logsHtml += `
    <h3>2.4 Step 4 – Current Knowledge & Selected MLUs</h3>
    <p>
      This section reflects which MLUs the learner or facilitator indicated as
      <em>already completed or planned</em> when using the tool.
    </p>
  `;

  const current = currentKnowledge || {};
  logsHtml += `
    <h4>2.4.1 Education (EDU)</h4>
    ${renderMluSelectionTable(
    current.education,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.4.2 Ethics, Inclusion & Democratisation (EID)</h4>
    ${renderMluSelectionTable(
    current.ethics,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.4.3 Governance (GOV)</h4>
    ${renderMluSelectionTable(
    current.governance,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.4.4 Technical Design (TEC)</h4>
    ${renderMluSelectionTable(
    current.technical,
    catalogLookup,
    suggestionLookup
  )}
  `;

  // Step 5 — Suggested MLUs with full names + reasons
  logsHtml += `
    <h3>2.5 Step 5 – Suggested MLUs & Reasons</h3>
    <p>
      In Step 5, the K8 Learning Journey Design Agent suggested MLUs per
      framework, with an explanation for why each MLU was selected.
    </p>
  `;

  const suggested = suggestedMlus || {};
  logsHtml += `
    <h4>2.5.1 Education (EDU)</h4>
    ${renderSuggestedMluTable(
    suggested.education,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.5.2 Ethics, Inclusion & Democratisation (EID)</h4>
    ${renderSuggestedMluTable(
    suggested.ethics,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.5.3 Governance (GOV)</h4>
    ${renderSuggestedMluTable(
    suggested.governance,
    catalogLookup,
    suggestionLookup
  )}

    <h4>2.5.4 Technical Design (TEC)</h4>
    ${renderSuggestedMluTable(
    suggested.technical,
    catalogLookup,
    suggestionLookup
  )}
  `;

  // Step 6 — Customisation & finalisation
  logsHtml += `
    <h3>2.6 Step 6 – Customisation & Finalisation</h3>
    <p>
      In Step 6, the facilitator curated a final shortlist of MLUs and
      optionally indicated for each MLU whether it is an
      <em>AI-only</em>, <em>hybrid</em>, or <em>human-only</em> selection.
    </p>
  `;

  // 2.6.1 Notes (combined or per framework)
  if (customisationNotes && typeof customisationNotes === "object") {
    logsHtml += `
      <h4>2.6.1 Notes by framework</h4>
      <p><strong>Education (EDU)</strong></p>
      ${textToParagraphHtml(customisationNotes.education || "")}
      <p><strong>Governance (GOV)</strong></p>
      ${textToParagraphHtml(customisationNotes.governance || "")}
      <p><strong>Technical Design (TEC)</strong></p>
      ${textToParagraphHtml(customisationNotes.technical || "")}
      <p><strong>Ethics, Inclusion & Democratisation (EID)</strong></p>
      ${textToParagraphHtml(customisationNotes.ethics || "")}
    `;
  } else if (customisationNotes) {
    logsHtml += `
      <h4>2.6.1 Notes</h4>
      ${textToParagraphHtml(customisationNotes)}
    `;
  } else {
    logsHtml += "<p>(no customisation notes recorded)</p>";
  }

  // 2.6.2 Final selection with selection type & full names
  if (step6SelectedMlus && typeof step6SelectedMlus === "object") {
    logsHtml += `
      <h4>2.6.2 Final selection of MLUs</h4>
      <p>
        These MLUs represent the <em>curated shortlist</em> that was carried
        forward to Step 7 for assembling the teaching plan. Where available,
        the selection type indicates whether the MLU is based on an
        AI suggestion, human decision, or a hybrid of both.
      </p>

      <h5>Education (EDU)</h5>
      ${renderStep6SelectionTable(
      step6SelectedMlus.education,
      catalogLookup,
      suggestionLookup,
      aiBaselineByFramework.education
    )}

      <h5>Ethics, Inclusion & Democratisation (EID)</h5>
      ${renderStep6SelectionTable(
      step6SelectedMlus.ethics,
      catalogLookup,
      suggestionLookup,
      aiBaselineByFramework.ethics
    )}

      <h5>Governance (GOV)</h5>
      ${renderStep6SelectionTable(
      step6SelectedMlus.governance,
      catalogLookup,
      suggestionLookup,
      aiBaselineByFramework.governance
    )}

      <h5>Technical Design (TEC)</h5>
      ${renderStep6SelectionTable(
      step6SelectedMlus.technical,
      catalogLookup,
      suggestionLookup,
      aiBaselineByFramework.technical
    )}
    `;
  }

  // Additional feedback if present
  if (feedback) {
    logsHtml += `
      <h3>2.7 Additional Feedback</h3>
      ${textToParagraphHtml(feedback)}
    `;
  }

  // ---- 3. MLU catalog per framework (EDU → EID → GOV → TEC) ----
  let catalogHtml = `
    <h2>3. Microlearning Unit Catalog</h2>
    <p>
      This section lists the MLUs that the K8 Learning Journey Design Agent
      can work with, grouped by framework. Use this catalog to explore
      alternative MLUs, swap entries in the teaching plan, or plan follow-up
      learning journeys.
    </p>
  `;

  const catalog = catalogByFramework || {};
  const frameworkOrder = [
    { key: "education", label: "Education (EDU)" },
    { key: "ethics", label: "Ethics, Inclusion & Democratisation (EID)" },
    { key: "governance", label: "Governance (GOV)" },
    { key: "technical", label: "Technical Design (TEC)" },
  ];

  frameworkOrder.forEach(({ key, label }, idx) => {
    catalogHtml += `
      <h3>3.${idx + 1} ${label}</h3>
      ${renderCatalogTable(catalog[key])}
    `;
  });

  // ---- Full HTML document (Word-friendly) ----
  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Twin4Resilience Learning Journey</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 12pt;
            line-height: 1.4;
          }
          h1 {
            font-size: 20pt;
            margin-bottom: 0.5em;
          }
          h2 {
            font-size: 16pt;
            margin-top: 1.5em;
            margin-bottom: 0.5em;
          }
          h3 {
            font-size: 14pt;
            margin-top: 1em;
            margin-bottom: 0.4em;
          }
          h4 {
            font-size: 13pt;
            margin-top: 0.8em;
            margin-bottom: 0.3em;
          }
          h5 {
            font-size: 12pt;
            margin-top: 0.6em;
            margin-bottom: 0.2em;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 1em;
          }
          th, td {
            border: 1px solid #555;
            padding: 4px;
            vertical-align: top;
          }
          th {
            background-color: #f0f0f0;
          }
          ul {
            margin-top: 0;
          }
          p {
            margin-top: 0.2em;
            margin-bottom: 0.6em;
          }
        </style>
      </head>
      <body>
        <h1>Twin4Resilience Learning Journey</h1>

        <div style="background-color: #f5f5f5; border: 2px solid #424242; border-radius: 8px; padding: 16px 24px; margin-bottom: 2em;">
          <h2 style="margin-top: 0.3em; margin-bottom: 0.5em; color: #212121;">Instructions</h2>
          <p style="margin-bottom: 0.3em;"><strong>1. Scan the journey overview and table.</strong> Check if the sequence of MLUs follows a story that makes sense for your learners.</p>
          <p style="margin-bottom: 0.3em;"><strong>2. Align with goals and roles.</strong> Compare each session with your organisational goals and the future role profile. Mark rows that need to be adapted or removed.</p>
          <p style="margin-bottom: 0.3em;"><strong>3. Adapt to your local setting.</strong> Adjust the example teaching methods, durations, and examples so they fit your community, language, and available tools.</p>
          <p style="margin-bottom: 0.3em;"><strong>4. Finalize and share.</strong> Once edited, save the document under your organisation's naming conventions and share it with colleagues as a starting point for further refinement.</p>
          <p style="margin-top: 0.8em; font-style: italic; color: #616161;">This document was generated by an AI-supported tool. Review all outputs with your professional judgement. You remain responsible for the final learning journey.</p>
        </div>

        <h2>1. Assembled Teaching Journey (Step 7 Output)</h2>
        ${mapImageData ? `
        <h3>Journey Map</h3>
        <img src="${mapImageData}" alt="Journey Map" style="max-width: 100%; height: auto; margin-bottom: 2em; border: 1px solid #ccc;" />
        ` : ""}
        <p>
          This section presents the teaching plan generated in Step 7. Use it
          as a starting point and adapt it in Microsoft Word to match your
          timetable, local context, and organisational guidelines.
        </p>
        <div>
          ${planHtml}
        </div>

        <h2>2. Session Context & Logs (Steps 1–6)</h2>
        <div>
          ${logsHtml}
        </div>

        ${catalogHtml}
      </body>
    </html>
  `;

  return html;
}

// ---------- Component ----------

export default function DownloadJourneyButton({
  finalMd,
  journeySummary,
  disabled,
  onDownload,
}) {
  const [isCapturing, setIsCapturing] = useState(false);

  const handleClick = async () => {
    if (disabled || isCapturing) return;
    setIsCapturing(true);

    let mapImageData = null;
    try {
      const captureNode = document.getElementById("journey-map-capture-node");
      if (captureNode) {
        // Dynamically measure the actual full width/height needed since the grid can expand past 1200
        const width = captureNode.scrollWidth;
        const height = captureNode.scrollHeight;

        // Use dom-to-image for robust CSS Grid & text rendering instead of canvas drawing
        mapImageData = await domtoimage.toPng(captureNode, {
          bgcolor: "#fafafa",
          width: width,
          height: height,
          style: {
            width: `${width}px`,
            height: `${height}px`,
            margin: 0,
            transform: "scale(1)",
            transformOrigin: "top left"
          }
        });
      }
    } catch (err) {
      console.error("Failed to capture Journey Map image", err);
    }

    const htmlDoc = buildJourneyHtmlDoc(finalMd, journeySummary || {}, mapImageData);

    const blob = new Blob([htmlDoc], {
      type: "application/msword;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "t4r_learning_journey.doc";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (onDownload) onDownload();

    setIsCapturing(false);
  };

  return (
    <Button
      variant="outlined"
      size="large"
      startIcon={isCapturing ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
      onClick={handleClick}
      disabled={disabled || isCapturing}
      sx={{
        borderRadius: 9999,
        px: 3,
        textTransform: "none",
        fontWeight: 600,
        bgcolor: "background.paper",
        border: "none",
        "&:hover": { bgcolor: "grey.100", border: "none" },
      }}
    >
      {isCapturing ? "Exporting Image..." : "Download Journey"}
    </Button>
  );
}
